import { Location } from '@angular/common';
import { inject, Injectable, provideAppInitializer } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

export interface RouteHistoryItem {
  url: string;
  timestamp: Date;
  queryParams?: any;
  fragment?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class RouteHistoryService {
  private history: RouteHistoryItem[] = [];
  private maxHistorySize = 50;

  constructor(
    private router: Router,
    private location: Location,
    private activatedRoute: ActivatedRoute,
  ) {}

  /**
   * Initialize route history tracking - called automatically in constructor
   */
  initializeHistoryTracking(): void {
    // Add current route to history on service initialization
    this.addToHistory(this.router.url);

    // Listen to router events and track navigation
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.addToHistory(event.urlAfterRedirects);
      });
  }

  /**
   * Add a route to the history
   */
  private addToHistory(url: string): void {
    const historyItem: RouteHistoryItem = {
      url: url,
      timestamp: new Date(),
      queryParams: this.activatedRoute.snapshot.queryParams,
      fragment: this.activatedRoute.snapshot.fragment,
    };

    // Avoid duplicating consecutive identical routes
    const lastItem = this.history[this.history.length - 1];
    if (!lastItem || lastItem.url !== url) {
      this.history.push(historyItem);

      // Maintain max history size
      if (this.history.length > this.maxHistorySize) {
        this.history.shift(); // Remove oldest item
      }
    }
  }

  /**
   * Get the complete route history
   */
  getHistory(): RouteHistoryItem[] {
    return [...this.history]; // Return a copy to prevent external modifications
  }

  /**
   * Get the current route (last item in history)
   */
  getCurrentRoute(): RouteHistoryItem | null {
    return this.history.length > 0 ? this.history[this.history.length - 1] : null;
  }

  /**
   * Remove and return the last item from history
   */
  removeLastItem(): RouteHistoryItem | null {
    return this.history.length > 0 ? this.history.pop() || null : null;
  }

  /**
   * Get the previous route
   */
  getPreviousRoute(): RouteHistoryItem | null {
    return this.history.length > 1 ? this.history[this.history.length - 2] : null;
  }

  /**
   * Navigate back to the previous route
   */
  goBack(): void {
    const previousRoute = this.getPreviousRoute();
    if (previousRoute) {
      // Remove current route from history before navigating back
      this.history.pop();
      this.router.navigateByUrl(previousRoute.url);
    } else {
      // Fallback to browser back if no previous route in our history
      this.location.back();
    }
  }

  /**
   * Navigate back using browser history (alternative approach)
   */
  goBackBrowser(): void {
    this.location.back();
  }

  /**
   * Check if there's a previous route available
   */
  hasPreviousRoute(): boolean {
    return this.history.length > 1;
  }

  /**
   * Clear the route history
   */
  clearHistory(): void {
    this.history = [];
  }

  /**
   * Get history count
   */
  getHistoryCount(): number {
    return this.history.length;
  }

  /**
   * Get routes visited in the last N minutes
   */
  getRecentRoutes(minutes: number): RouteHistoryItem[] {
    const cutoffTime = new Date(Date.now() - minutes * 60000);
    return this.history.filter(item => item.timestamp >= cutoffTime);
  }

  /**
   * Check if a specific route exists in history
   */
  hasVisited(url: string): boolean {
    return this.history.some(item => item.url === url);
  }

  /**
   * Get the last time a route was visited
   */
  getLastVisitTime(url: string): Date | null {
    for (let i = this.history.length - 1; i >= 0; i--) {
      if (this.history[i].url === url) {
        return this.history[i].timestamp;
      }
    }
    return null;
  }

  /**
   * Set maximum history size
   */
  setMaxHistorySize(size: number): void {
    this.maxHistorySize = Math.max(1, size);

    // Trim history if it exceeds new max size
    if (this.history.length > this.maxHistorySize) {
      this.history = this.history.slice(-this.maxHistorySize);
    }
  }

  /**
   * Get unique routes from history
   */
  getUniqueRoutes(): string[] {
    return [...new Set(this.history.map(item => item.url))];
  }

  /**
   * Navigate to a specific route
   */
  navigateTo(url: string): void {
    this.router.navigateByUrl(url);
  }

  /**
   * Replace current route in history (useful for redirects)
   */
  replaceCurrentRoute(url: string): void {
    if (this.history.length > 0) {
      this.history[this.history.length - 1] = {
        url: url,
        timestamp: new Date(),
        queryParams: this.activatedRoute.snapshot.queryParams,
        fragment: this.activatedRoute.snapshot.fragment,
      };
    }
  }
}

export const routeHistoryProviders = provideAppInitializer(() => {
  const routeHistoryService = inject(RouteHistoryService);
  routeHistoryService.initializeHistoryTracking();
  return Promise.resolve();
});
